import { useState, useRef, useImperativeHandle, forwardRef } from 'react';

import { useParams } from 'react-router-dom';

import {
  Row,
  Col,
  Input,
  Select,
  InputNumber,
  Typography,
  Tooltip,
} from 'antd';
import { sortBy } from 'lodash';
import { FileTextOutlined } from '@ant-design/icons';

import { Form, WrapperSelect, CustomNotification } from 'common/components';
import NutrientList from './NutrientList';

import { useQueryFactsPanel } from '../../multiple-panel/facts-panel/useQueryFactsPanel';

import { useGetProductEnums, useProductFullView } from 'hooks';
import { SCREEN_WIDTH, useWindowSize } from 'hooks/windowSize';
import { useInvalidateGroupDetail } from '../../shared/hooks';
import { useAddNutritionLabel } from '../hook/useAddNutritionLabel';
import { useFactPanelQasRequiredFields } from '../hook/useFactPanelQasRequiredFields';

import { deleteObjectField } from 'utils';

import { DEFAULT_FOOTER_NOTE } from '../DailyValueConstants';
import { NUTRITION_LABEL_TYPE } from 'static/Constants';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import { TOOlTIP_FIELDS } from 'static/tooltipFields';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { getFormattedPath } from 'pages/qa-spec/components/shared/QaSpecHelper';

const { Option } = Select;
const { Paragraph } = Typography;

const INITIAL_VALUE = {
  servingSizeMeasurement: 'Gram',
  nutrientFormatType: 'US_FDA_NFP_2020_STANDARD',
};

const AddNutritionLabel = forwardRef((props, forwardedRef) => {
  const dvFormRef = useRef();
  const [form] = Form.useForm();

  const { id } = useParams();

  const {
    visible,
    typeView,
    productId,
    setIsUpdating,
    setIsAddingNew,
    factsPanel,
    onSubmitSuccess,
    onSubmitFail,
  } = props;

  const { productEnums } = useGetProductEnums();
  const { productFull } = useProductFullView({ productId: id });
  const [screenWidth, _] = useWindowSize();

  const [nutritionFactProperties, setNutritionFactProperties] = useState([]);
  const { listFactsPanel } = useQueryFactsPanel({ id, typeView });
  const { handleSyncAllProductDetailGroup, handleSyncAllQaDetailGroup } =
    useInvalidateGroupDetail(id);

  const mutation = useAddNutritionLabel({ productId: id, typeView });

  const dailyValuesField = form.getFieldValue('nutritionFactProperties') || [];

  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId,
    typeView,
    section: 'qaSpecification.factsPanel.nutritionLabel',
  });

  const initForm = (on) => {
    if (on === 'edit') initEditLabel();

    if (on === 'add') initAddLabel();
  };

  const initEditLabel = () => {
    form.setFieldsValue({
      ...factsPanel,
      nutrientFormatType: factsPanel.nutrientFormatType
        ? factsPanel.nutrientFormatType
        : 'US_FDA_NFP_2020_STANDARD',
      servingSizeMeasurement: factsPanel.servingSizeMeasurement
        ? factsPanel.servingSizeMeasurement
        : 'Gram',
    });
    setNutritionFactProperties(factsPanel?.nutritionFactProperties);
  };

  const initAddLabel = () => {
    setNutritionFactProperties([]);
    form.setFieldsValue({
      ...INITIAL_VALUE,
      nutrientFormatType:
        primaryFactsPanel?.nutrientFormatType || 'US_FDA_NFP_2020_STANDARD',
      languageCode: 'en',
    });
  };

  const handleRemoveDV = (ingredient) => {
    const dv = form.getFieldValue('nutritionFactProperties');
    const removeIngredient = dv.filter(
      (dv) => dv.nutritionTypeCode !== ingredient.nutritionTypeCode,
    );

    form.setFieldsValue({
      ...form.getFieldsValue(),
      nutritionFactProperties: removeIngredient,
    });

    setNutritionFactProperties(removeIngredient);
  };

  const submitForm = async () => {
    //const values = await form.validateFields();
    const values = form.getFieldsValue(true);

    //if (nutritionFactProperties?.length > 0) {
    const productId = productFull?.productId;

    const paramsKey = {
      overview: {
        id: 'productItemId',
        data: 'data',
        nutritionFacts: 'properties',
      },
      'qa-spec': {
        id: 'productId',
        data: 'nutritionFact',
        nutritionFacts: 'nutritionFactProperties',
      },
    };

    const id = factsPanel?.id ? factsPanel?.id : null;
    const params = {
      [paramsKey[typeView].id]: productId,
      [paramsKey[typeView].data]: {
        ...values,
        id,
        nutritionFactProperties,
        nutrientFormatType: values.nutrientFormatType,
        [paramsKey[typeView].nutritionFacts]: nutritionFactProperties,
      },
    };

    mutation.mutate(params, {
      onSuccess: (result) => {
        if (result.isSuccess) {
          form.resetFields();
          setNutritionFactProperties([]);
          handleSyncAllProductDetailGroup();
          handleSyncAllQaDetailGroup();
          onSubmitSuccess && onSubmitSuccess();

          CustomNotification.success('Update Nutrition Fact Success.');
        } else {
          form.resetFields();
          setNutritionFactProperties([]);

          onSubmitFail && onSubmitFail();

          CustomNotification.error('Something went wrong!');
        }
      },
    });
    // } else {
    //   dvFormRef?.current?.validateForm();
    //   CustomNotification.error('Please add at least one nutrient!');
    //   return;
    // }
  };

  const resetForm = () => {
    form.resetFields();
    setNutritionFactProperties([]);
  };

  const onFormProviderFinish = (name, { values, forms }) => {
    if (name === 'dvForm') {
      const { labelForm } = forms;
      const dv = labelForm.getFieldValue('nutritionFactProperties');
      const { dailyValueIntakePercent, quantity, nutrientIdx } = values;

      let newDv = {
        ...values,
        dailyValueIntakePercent: dailyValueIntakePercent ?? null,
        quantity: quantity ?? null,
      };

      deleteObjectField(newDv, ['nutrientIdx']);

      let properties = dv ? dv : '';

      if (nutrientIdx === null) {
        labelForm.setFieldsValue({
          nutritionFactProperties: [...properties, newDv],
        });

        setNutritionFactProperties([...nutritionFactProperties, newDv]);
        setIsAddingNew(false);
      } else {
        properties[nutrientIdx] = newDv;

        labelForm.setFieldsValue({
          nutritionFactProperties: properties,
        });

        setNutritionFactProperties(properties);
        setIsUpdating(false);
      }
    }
  };

  const handleFooterNote = () => {
    form.setFieldsValue({
      ...form.getFieldsValue(),
      fdaLabelFootNote: DEFAULT_FOOTER_NOTE,
    });
  };

  const productBasicUOM = productEnums?.filter(
    (type) => type.enumName === 'BasicUomEnum',
  );

  const productNutrientBasisQuantityTypeCodeEnum = productEnums?.find(
    (type) => type.enumName === 'NutrientBasisQuantityTypeCodeEnum',
  );

  const servingPerContainerDescriptorEnum = productEnums?.find(
    (type) => type.enumName === 'ServingPerContainerDescriptorEnum',
  );

  const dv = Array.isArray(nutritionFactProperties)
    ? nutritionFactProperties
    : [];

  const [primaryFactsPanel] = listFactsPanel;

  const existingFormatType = NUTRITION_LABEL_TYPE.filter(
    (item) => item?.value === primaryFactsPanel?.nutrientFormatType,
  );

  const numberOfFactPanelToCompare = factsPanel ? 2 : 1;
  const nutrientFormatTypeOptions =
    existingFormatType?.length &&
      listFactsPanel?.length >= numberOfFactPanelToCompare
      ? existingFormatType
      : NUTRITION_LABEL_TYPE;

  const languageCodeEnum = productEnums?.filter(
    (type) => type.enumName === 'LanguageCodeEnum',
  );

  useImperativeHandle(forwardedRef, () => {
    return {
      initForm,
      submitForm,
      resetForm,
    };
  });

  return visible ? (
    <Form.Provider onFormFinish={onFormProviderFinish}>
      <Form layout='vertical' name='labelForm' form={form} preserve={false}>
        <Row gutter={16}>
          <Col span={8}>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        TOOlTIP_FIELDS.labelType.fieldPath,
                      )}
                      qaSpecFieldName='Label Type'
                    >
                      <QaSpecHelpContentLabel
                        fieldPath={TOOlTIP_FIELDS.labelType.fieldPath}
                      >
                        Label Type
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='nutrientFormatType'
                  rules={[
                    getRequiredRule({
                      fieldName: ['nutrientFormatType'],
                      displayName: 'Label Type',
                    }),
                  ]}
                >
                  <WrapperSelect
                    defaultValue={nutrientFormatTypeOptions?.[0].value}
                    getPopupContainer={(triggerNode) => {
                      return triggerNode;
                    }}
                  >
                    {[
                      nutrientFormatTypeOptions.map((labelType) => {
                        return (
                          <Option value={labelType?.value}>
                            {labelType?.label}
                          </Option>
                        );
                      }),
                    ]}
                  </WrapperSelect>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.nutrientBasisQuantityTypeCode',
                      )}
                      qaSpecFieldName=' Nutrient Basis Quantity Type Code'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.nutrientBasisQuantityTypeCode'>
                        Nutrient Basis Quantity Type Code
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='nutrientBasisQuantityTypeCode'
                  rules={[
                    getRequiredRule({
                      fieldName: ['nutrientBasisQuantityTypeCode'],
                      displayName: 'Nutrient Basis Quantity Type Code',
                    }),
                  ]}
                >
                  <WrapperSelect
                    getPopupContainer={(triggerNode) => {
                      return triggerNode;
                    }}
                  >
                    {sortBy(
                      productNutrientBasisQuantityTypeCodeEnum?.enumProperties,
                      ['enumDisplayName', 'enumGroup'],
                    ).map((item, index) => (
                      <Option value={item?.enumDisplayName} key={index}>
                        {item?.enumDisplayName}
                      </Option>
                    ))}
                  </WrapperSelect>
                </Form.Item>
              </Col>
              <Col lg={24} xl={12}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.nutrientBasisValue',
                      )}
                      qaSpecFieldName='Nutrient Basis'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.nutrientBasisValue'>
                        Nutrient Basis
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='nutrientBasisValue'
                  rules={[
                    getRequiredRule({
                      fieldName: ['nutrientBasisValue'],
                      displayName: 'Nutrient Basis',
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    style={styles.inputNumber}
                  />
                </Form.Item>
              </Col>
              <Col lg={24} xl={12}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.unitOfMeasure',
                      )}
                      qaSpecFieldName='Unit of Measure'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.unitOfMeasure'>
                        Unit of Measure
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='unitOfMeasure'
                  rules={[
                    getRequiredRule({
                      fieldName: ['unitOfMeasure'],
                      displayName: 'Unit of Measure',
                    }),
                  ]}
                >
                  <WrapperSelect
                    showSearch
                    filterOption={(input, option) =>
                      option?.value
                        ?.toLowerCase()
                        .indexOf(input?.toLowerCase()) >= 0
                    }
                    getPopupContainer={(triggerNode) => {
                      return triggerNode;
                    }}
                  >
                    {sortBy(
                      productBasicUOM?.[0]?.enumProperties?.filter((type) =>
                        ['Mass', 'Volume'].includes(type.enumGroup),
                      ),
                      ['enumDisplayName', 'enumGroup'],
                    )?.map((item, index) => (
                      <Option value={item?.enumDisplayName} key={index}>
                        {item?.enumDisplayName}
                      </Option>
                    ))}
                  </WrapperSelect>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.servingPerContainerDescriptor',
                      )}
                      qaSpecFieldName='S/C Descriptor'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.servingPerContainerDescriptor'>
                        {screenWidth > SCREEN_WIDTH.EXTRA_LARGE
                          ? 'Servings Per Container Descriptor'
                          : 'S/C Descriptor'}
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='servingPerContainerDescriptor'
                  rules={[
                    getRequiredRule({
                      fieldName: ['servingPerContainerDescriptor'],
                      displayName: 'S/C Descriptor',
                    }),
                  ]}
                >
                  <WrapperSelect
                    getPopupContainer={(triggerNode) => {
                      return triggerNode;
                    }}
                  >
                    {sortBy(servingPerContainerDescriptorEnum?.enumProperties, [
                      'enumDisplayName',
                      'enumGroup',
                    ]).map((item, index) => (
                      <Option value={item?.enumDisplayName} key={index}>
                        {item?.enumDisplayName}
                      </Option>
                    ))}
                  </WrapperSelect>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.numberOfServingsPerContainer',
                      )}
                      qaSpecFieldName='S/C'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.supplementInformation.numberOfServingsPerContainer'>
                        {screenWidth > SCREEN_WIDTH.EXTRA_LARGE
                          ? 'Servings Per Container'
                          : 'S/C'}
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='numberOfServingsPerPackage'
                  rules={[
                    getRequiredRule({
                      fieldName: ['numberOfServingsPerPackage'],
                      displayName: 'Servings Per Container',
                    }),
                    ({ getFieldValue }) => ({
                      validator(rule, value) {
                        if (Number(value) > 9999) {
                          return Promise.reject([
                            'The maximum allowed value for servings per container is 9999.',
                          ]);
                        } else {
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={9999}
                    style={styles.inputNumber}
                    formatter={(value) => {
                      if (+value > 0)
                        return (+value)?.toFixed(4)?.replace(/(\.0+|0+)$/, '');
                      return value;
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={24} xl={12}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.servingSizeDescription',
                      )}
                      qaSpecFieldName='Description'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.servingSizeDescription'>
                        Description
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='servingSizeDescription'
                  rules={[
                    getRequiredRule({
                      fieldName: ['servingSizeDescription'],
                      displayName: 'Description',
                    }),
                  ]}
                >
                  <Input.TextArea rows={2} maxLength={100} />
                </Form.Item>
              </Col>
              <Col lg={24} xl={12}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.servingSize',
                      )}
                      qaSpecFieldName='Size'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.servingSize'>
                        Size
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='servingSize'
                  rules={[
                    getRequiredRule({
                      fieldName: ['servingSize'],
                      displayName: 'Size',
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={9999}
                    style={styles.inputNumber}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col lg={24} xl={12}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.servingSizeMeasurement',
                      )}
                      qaSpecFieldName='Size Unit'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.servingSizeMeasurement'>
                        Size Unit
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='servingSizeMeasurement'
                  rules={[
                    getRequiredRule({
                      fieldName: ['servingSizeMeasurement'],
                      displayName: 'Size Unit',
                    }),
                  ]}
                >
                  <WrapperSelect
                    showSearch
                    filterOption={(input, option) =>
                      option?.value
                        ?.toLowerCase()
                        .indexOf(input?.toLowerCase()) >= 0
                    }
                    getPopupContainer={(triggerNode) => {
                      return triggerNode;
                    }}
                  >
                    {sortBy(
                      productBasicUOM?.[0]?.enumProperties?.filter((type) =>
                        ['Mass', 'Volume'].includes(type.enumGroup),
                      ),
                      ['enumDisplayName', 'enumGroup'],
                    )?.map((item, index) => (
                      <Option value={item?.enumDisplayName} key={index}>
                        {item?.enumDisplayName}
                      </Option>
                    ))}
                  </WrapperSelect>
                </Form.Item>
              </Col>
              <Col lg={24} xl={12}>
                <Form.Item
                  label={
                    <TraceableCommentContainer
                      qaSpecName={getFormattedPath(
                        'qaSpecification.factPanels.nutritionalInformation.calories',
                      )}
                      qaSpecFieldName='Calories'
                    >
                      <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.calories'>
                        Calories
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  name='calories'
                  rules={[
                    getRequiredRule({
                      fieldName: ['calories'],
                      displayName: 'Calories',
                    }),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={9999}
                    style={styles.inputNumber}
                    precision={2}
                  />
                </Form.Item>
              </Col>

              <Col>
                <Form.Item
                  label='Special Character'
                  style={{ display: 'flex' }}
                >
                  <Paragraph copyable={{ text: '*' }}>
                    * Percent Daily Values are based a 2,000 calories diet.
                  </Paragraph>
                  <Paragraph copyable={{ text: '†' }}>
                    † Percent Daily Value not established
                  </Paragraph>
                  <Paragraph copyable={{ text: '™' }}>™ Trademark</Paragraph>
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={8} style={{ height: '100%' }}>
            <NutrientList
              ref={dvFormRef}
              dv={dv}
              isEdit
              productId={productId}
              typeView={typeView}
              dailyValuesField={dailyValuesField}
              setNutritionFactProperties={setNutritionFactProperties}
              handleRemoveDV={handleRemoveDV}
            />
          </Col>
          {/* // Start Vitamins */}
          <Col span={8}>
            <Col span={24}>
              <Form.Item
                label={
                  <TraceableCommentContainer
                    qaSpecName={getFormattedPath(
                      'qaSpecification.factPanels.nutritionalInformation.ingredientNotice',
                    )}
                    qaSpecFieldName='Ingredient Notice'
                  >
                    <QaSpecHelpContentLabel fieldPath='qaSpecification.factPanels.nutritionalInformation.ingredientNotice'>
                      Ingredient Notice
                    </QaSpecHelpContentLabel>
                  </TraceableCommentContainer>
                }
                name='ingredientNotice'
                rules={[
                  getRequiredRule({
                    fieldName: ['ingredientNotice'],
                    displayName: 'Ingredient Notice',
                  }),
                ]}
              >
                <Input.TextArea rows={5} maxLength={300} />
              </Form.Item>
              <Form.Item
                label={
                  <TraceableCommentContainer
                    qaSpecName={getFormattedPath(
                      TOOlTIP_FIELDS.nutritionFdaLabelFootNote.fieldPath,
                    )}
                    qaSpecFieldName='Footer Note'
                  >
                    <QaSpecHelpContentLabel
                      fieldPath={
                        TOOlTIP_FIELDS.nutritionFdaLabelFootNote.fieldPath
                      }
                    >
                      <span>
                        Footer Note
                        <Tooltip title='Use Default Note'>
                          <FileTextOutlined
                            onClick={handleFooterNote}
                            style={{ marginLeft: '5px' }}
                          />
                        </Tooltip>
                      </span>
                    </QaSpecHelpContentLabel>
                  </TraceableCommentContainer>
                }
                name='fdaLabelFootNote'
                rules={[
                  getRequiredRule({
                    fieldName: ['fdaLabelFootNote'],
                    displayName: 'Footer Note',
                  }),
                ]}
              >
                <Input.TextArea rows={5} maxLength={300} />
              </Form.Item>

              <Form.Item
                name='languageCode'
                label={
                  <TraceableCommentContainer
                    qaSpecName={getFormattedPath(
                      TOOlTIP_FIELDS.factPanelsLanguageCode.fieldPath,
                    )}
                    qaSpecFieldName='Language Code'
                  >
                    <QaSpecHelpContentLabel
                      fieldPath={
                        TOOlTIP_FIELDS.factPanelsLanguageCode.fieldPath
                      }
                    >
                      Language Code
                    </QaSpecHelpContentLabel>
                  </TraceableCommentContainer>
                }
                rules={[
                  getRequiredRule({
                    fieldName: ['languageCode'],
                    displayName: 'Language Code',
                  }),
                ]}
              >
                <WrapperSelect
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      ?.toLowerCase()
                      .indexOf(input?.toLowerCase()) >= 0
                  }
                  optionFilterProp='children'
                  getPopupContainer={(triggerNode) => {
                    return triggerNode;
                  }}
                >
                  {sortBy(languageCodeEnum?.[0]?.enumProperties, [
                    'enumDisplayName',
                    'enumGroup',
                  ])?.map((item, index) => (
                    <Option value={item?.enumCode} key={index}>
                      {`${item.enumDescription
                        } - (${item.enumCode.toUpperCase()})`}
                    </Option>
                  ))}
                </WrapperSelect>
              </Form.Item>
            </Col>
          </Col>
        </Row>
      </Form>
    </Form.Provider>
  ) : null;
});

const styles = {
  inputNumber: {
    width: '100%',
  },
  selectAfter: {
    width: '72px',
  },
  li: {
    display: 'flex',
    justifyContent: 'flex-end',
    border: '1px solid #ddd',
    padding: '8px 12px',
    borderRadius: '4px',
    alignItems: 'center',
  },
  unit: {
    padding: '5px 8px',
    marginLeft: '5px',
    background: '#DCDCDC',
    borderRadius: '20px',
  },
  formItem: {
    marginBottom: '0px',
  },
  dv: {
    textAlign: 'center',
  },
};

export default AddNutritionLabel;
